import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { TrendingUp, Users, AlertTriangle, ChevronRight, BarChart3, PieChart, Activity, Share2 } from 'lucide-react'
import { useCountUp, useInView } from '../hooks'
import { dashboardAPI } from '../utils/api'

// 统计数据配置（后台可配置）
const DEFAULT_STATS = {
  applicationRate: { value: 12.5, suffix: '%', label: '网申通过率', source: '2025届应届生求职调研' },
  interviewRate: { value: 8.3, suffix: '%', label: '面试录取率', source: '央国企HR调研数据' },
  totalApplicants: { value: 8740000, suffix: '人', label: '2025届毕业生总数', source: '教育部统计数据' },
  avgPositions: { value: 156, suffix: '个', label: '人均投递岗位数', source: '平台用户数据' }
}

// 被刷主要原因TOP5
const REJECTION_REASONS = [
  { reason: '简历与岗位匹配度低', percentage: 42, color: '#dc2626' },
  { reason: '缺乏相关实习经历', percentage: 28, color: '#f59e0b' },
  { reason: '笔试成绩不达标', percentage: 15, color: '#0ea5e9' },
  { reason: '面试表现不佳', percentage: 10, color: '#8b5cf6' },
  { reason: '学校/专业不符合要求', percentage: 5, color: '#6b7280' }
]

// 行业竞争比数据
const INDUSTRY_DATA = [
  { industry: '金融央企', ratio: 85, color: '#dc2626' },
  { industry: '能源央企', ratio: 62, color: '#f59e0b' },
  { industry: '通信央企', ratio: 55, color: '#0ea5e9' },
  { industry: '建筑央企', ratio: 38, color: '#10b981' },
  { industry: '制造央企', ratio: 28, color: '#8b5cf6' }
]

const AnimatedNumber = ({ value, suffix, duration = 2000 }) => {
  const [ref, isInView] = useInView()
  const count = useCountUp(isInView ? value : 0, duration)
  
  const formatNumber = (num) => {
    if (num >= 10000) {
      return (num / 10000).toFixed(1) + '万'
    }
    return num.toLocaleString()
  }
  
  return (
    <span ref={ref} className="tabular-nums">
      {typeof value === 'number' && value < 100 ? count.toFixed(1) : formatNumber(count)}
      {suffix}
    </span>
  )
}

const StatCard = ({ icon: Icon, stat, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
    className="card p-6 text-center hover:scale-105 transition-transform"
  >
    <div className="w-12 h-12 mx-auto mb-4 bg-primary-100 rounded-xl flex items-center justify-center">
      <Icon className="text-primary-600" size={24} />
    </div>
    <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
      <AnimatedNumber value={stat.value} suffix={stat.suffix} />
    </div>
    <div className="text-sm text-gray-600 mb-1 whitespace-nowrap">{stat.label}</div>
    <div className="text-xs text-gray-400 whitespace-nowrap">数据来源: {stat.source}</div>
  </motion.div>
)

// SVG Pie Chart Component with Labels
const PieChartComponent = ({ data, delay }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  
  const total = data.reduce((sum, item) => sum + item.percentage, 0)
  let currentAngle = -90
  
  const slices = data.map((item, index) => {
    const angle = (item.percentage / total) * 360
    const startAngle = currentAngle
    const endAngle = currentAngle + angle
    const midAngle = startAngle + angle / 2
    currentAngle = endAngle
    
    const startRad = (startAngle * Math.PI) / 180
    const endRad = (endAngle * Math.PI) / 180
    const midRad = (midAngle * Math.PI) / 180
    
    const x1 = 140 + 100 * Math.cos(startRad)
    const y1 = 140 + 100 * Math.sin(startRad)
    const x2 = 140 + 100 * Math.cos(endRad)
    const y2 = 140 + 100 * Math.sin(endRad)
    const largeArc = angle > 180 ? 1 : 0
    
    const path = `M 140 140 L ${x1} ${y1} A 100 100 0 ${largeArc} 1 ${x2} ${y2} Z`
    
    // Label position - outside the pie
    const labelRadius = 125
    const labelX = 140 + labelRadius * Math.cos(midRad)
    const labelY = 140 + labelRadius * Math.sin(midRad)
    
    return { ...item, path, startAngle, endAngle, midAngle, midRad, labelX, labelY, index }
  })
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="card p-6 h-full flex flex-col"
    >
      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center whitespace-nowrap">
        <PieChart className="mr-2 text-brand-red flex-shrink-0" size={20} />
        网申被刷主要原因分布
      </h3>
      
      <div className="flex-1 flex flex-col items-center justify-center">
        {/* Pie Chart SVG with Labels */}
        <div className="relative">
          <svg width="400" height="320" viewBox="0 0 400 320">
            {slices.map((slice, index) => (
              <motion.path
                key={slice.reason}
                d={slice.path}
                fill={slice.color}
                stroke="white"
                strokeWidth="2"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: delay + index * 0.15, duration: 0.5 }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  transform: hoveredIndex === index ? 'scale(1.05)' : 'scale(1)',
                  transformOrigin: '140px 140px',
                  cursor: 'pointer',
                  filter: hoveredIndex === index ? 'brightness(1.1)' : 'none'
                }}
              />
            ))}
            
            {/* Center circle */}
            <circle cx="140" cy="140" r="60" fill="white" />
            
            <text x="140" y="130" textAnchor="middle" className="text-2xl font-bold fill-gray-800">
              {hoveredIndex !== null ? `${slices[hoveredIndex].percentage}%` : '合计'}
            </text>
            <text x="140" y="155" textAnchor="middle" className="text-sm fill-gray-500">
              {hoveredIndex !== null ? slices[hoveredIndex].reason : '100%'}
            </text>
            
            {/* Labels with lines */}
            {slices.map((slice, index) => {
              const isRight = slice.labelX >= 140
              const textX = isRight ? slice.labelX + 10 : slice.labelX - 10
              const textAnchor = isRight ? 'start' : 'end'
              
              return (
                <g key={`label-${index}`}>
                  {/* Line from pie to label */}
                  <line
                    x1={140 + 102 * Math.cos(slice.midRad)}
                    y1={140 + 102 * Math.sin(slice.midRad)}
                    x2={slice.labelX}
                    y2={slice.labelY}
                    stroke={slice.color}
                    strokeWidth="1.5"
                    opacity={hoveredIndex === index ? 1 : 0.7}
                  />
                  {/* Label dot */}
                  <circle
                    cx={slice.labelX}
                    cy={slice.labelY}
                    r="3"
                    fill={slice.color}
                    opacity={hoveredIndex === index ? 1 : 0.7}
                  />
                  {/* Reason text */}
                  <text
                    x={textX}
                    y={slice.labelY - 4}
                    textAnchor={textAnchor}
                    className="text-sm font-medium"
                    fill={slice.color}
                    opacity={hoveredIndex === index ? 1 : 0.9}
                  >
                    {slice.reason}
                  </text>
                  {/* Percentage text */}
                  <text
                    x={textX}
                    y={slice.labelY + 14}
                    textAnchor={textAnchor}
                    className="text-lg font-bold"
                    fill={slice.color}
                  >
                    {slice.percentage}%
                  </text>
                </g>
              )
            })}
          </svg>
        </div>
        
        {/* Bottom Legend */}
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          {data.map((item, index) => (
            <motion.div
              key={item.reason}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: delay + index * 0.1, duration: 0.4 }}
              className={`flex items-center px-3 py-1.5 rounded-full transition-all cursor-pointer ${
                hoveredIndex === index ? 'shadow-md scale-105' : ''
              }`}
              style={{ 
                backgroundColor: hoveredIndex === index ? item.color + '20' : item.color + '10',
                border: `1px solid ${hoveredIndex === index ? item.color : item.color + '40'}`
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div 
                className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs font-medium whitespace-nowrap" style={{ color: item.color }}>
                {item.reason} {item.percentage}%
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}

const BarChart = ({ data, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
    className="card p-6 h-full flex flex-col"
  >
    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center whitespace-nowrap">
      <BarChart3 className="mr-2 text-primary-600 flex-shrink-0" size={20} />
      各行业竞争比（人/岗）
    </h3>
    <div className="flex-1 flex flex-col justify-center space-y-5">
      {data.map((item, index) => (
        <div key={item.industry} className="flex items-center">
          <span className="w-24 text-sm text-gray-600 shrink-0 whitespace-nowrap">{item.industry}</span>
          <div className="flex-1 bg-gray-100 rounded-full h-7 mx-3 relative overflow-hidden">
            <motion.div
              className="h-full rounded-full flex items-center justify-end pr-3"
              style={{ backgroundColor: item.color }}
              initial={{ width: 0 }}
              animate={{ width: `${(item.ratio / 100) * 100}%` }}
              transition={{ delay: delay + index * 0.1, duration: 0.8 }}
            >
              <span className="text-white text-sm font-bold whitespace-nowrap">{item.ratio}:1</span>
            </motion.div>
          </div>
        </div>
      ))}
    </div>
  </motion.div>
)

const DashboardPage = () => {
  const navigate = useNavigate()
  const [stats, setStats] = useState(DEFAULT_STATS)
  const [participantCount, setParticipantCount] = useState(12847)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // const response = await dashboardAPI.getStats()
        // setStats(response.data)
      } catch (error) {
        console.log('使用默认统计数据')
      }
    }
    fetchStats()
    
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        setParticipantCount(prev => prev + 1)
      }
    }, 5000)
    
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 via-white to-gray-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-16 pb-12 md:pt-24 md:pb-16">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/5 to-primary-400/5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-6 whitespace-nowrap">
              <Activity size={16} className="mr-2 flex-shrink-0" />
              首月已有 <span className="font-bold mx-1">{participantCount.toLocaleString()}</span> 名同学参加了测评
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              应届生央国企求职
              <span className="text-primary-600">大数据洞察</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              基于2025届应届生真实求职数据，精准分析央国企招聘趋势，助你找到最适合的求职方向
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/match')}
              className="btn-primary text-lg px-8 py-4 inline-flex items-center whitespace-nowrap"
            >
              测测你的匹配度
              <ChevronRight size={20} className="ml-2" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* 核心数据 */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title whitespace-nowrap">2025届应届生求职关键数据</h2>
            <p className="section-subtitle">真实数据揭示央国企求职现状</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <StatCard icon={TrendingUp} stat={stats.applicationRate} delay={0} />
            <StatCard icon={Users} stat={stats.interviewRate} delay={0.1} />
            <StatCard icon={BarChart3} stat={stats.totalApplicants} delay={0.2} />
            <StatCard icon={PieChart} stat={stats.avgPositions} delay={0.3} />
          </div>
        </div>
      </section>

      {/* 被刷原因饼图 + 行业竞争 */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-stretch">
            {/* 被刷原因饼图 */}
            <PieChartComponent data={REJECTION_REASONS} delay={0} />

            {/* 行业竞争 */}
            <BarChart data={INDUSTRY_DATA} delay={0.2} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-primary-600 to-primary-500 rounded-2xl p-8 md:p-12 text-white"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              想知道你的央国企求职竞争力如何？
            </h2>
            <p className="text-primary-100 mb-8 text-lg">
              3分钟完成测评，获取你的专属匹配度报告
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/match')}
                className="bg-white text-primary-600 font-bold px-8 py-4 rounded-xl text-lg hover:bg-primary-50 transition-colors inline-flex items-center justify-center whitespace-nowrap"
              >
                立即免费测评
                <ChevronRight size={20} className="ml-2" />
              </motion.button>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/share')}
                className="bg-primary-700 text-white font-bold px-8 py-4 rounded-xl text-lg hover:bg-primary-800 transition-colors inline-flex items-center justify-center whitespace-nowrap cursor-pointer"
              >
                <Share2 size={20} className="mr-2" />
                分享赚收益
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default DashboardPage
